<template>
  <v-container v-if='loaded'
    fill-height
    fluid
    grid-list-xl>

    <v-dialog v-model="openAuditTrail" max-width="1400px">
      <app-audit-trail-timeline entity="siterequest" v-if='openAuditTrail' :entity_id="siterequest_id" :fields='config.fields'/>
    </v-dialog>

    <v-flex>

      <!-- 
        - i centri o EMN fanno una siterequest (NEW)
        - arrivano quindi le richieste ai depositi e ai fornitori che fanno da deposito via mail
          (per ogni farmaco, questo pdf lo salvo) (REQUESTED)
        - se il deposito e' online nella mail c'e' anche un link col token
          che permette al deposito di compilare i dettagli (lotto, seriali), ma solo
          dei farmaci che ha lui per quello studio ovviamente (mette tutto in siteresponse) (SENT).
          Se il deposito non e' online questa procedura la fa EMN.
        - Il centro conferma la ricezione con pdf e arriva una mail al deposito e a EMN (RECEIVED)
       -->

      <app-upsert
        ref='upsert'
        :config="config"
        :item="siterequest"
        :title="siterequest.protocol && siterequest.protocol.name"
        :subtitle="status"
        :hide-save="true">

        <template slot='headerActions'>
          <app-audit-trail-btn @click="openAuditTrail = true" />
          <app-patch v-if='isAdmin && siterequest.id && isPatchable' entity="siterequest" :entity_id="siterequest.id" :protocol_id="siterequest.protocol_id"/>

          <v-btn
            color="red"
            @click="$router.push('/siterequest')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>

        <template v-slot:actions="{ item, valid }">

          <template v-if="siterequest.status === 'CONFIRMED' || siterequest.status === 'SENT'"> 
            <v-chip class='mr-1' v-for='item in missingDrugs' :key='item.id' :color='item.color'> {{item.label}}</v-chip>
          </template>
          <v-menu offset-y
            v-if='(siterequest.status === "SENT" || siterequest.status === "CONFIRMED" || siterequest.status === "RECEIVED" || siterequest.status === "IMPORTED") && siterequest.siterequest_pdf'>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="purple darken-2 white--text"
                  v-bind="attrs"
                  v-on="on">
                  PDF
                  <v-icon right>mdi-file-pdf-box</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in siterequest.siterequest_pdf"
                  :href='`${BASE_URL}/pdf/${item}`'
                  :key="index">
                  <v-list-item-title>{{ item }}</v-list-item-title>
                </v-list-item>
              </v-list>
          </v-menu>

          <v-btn
            v-if="isAdmin && (siterequest.status === 'CONFIRMED' || siterequest.status === 'SENT')"
            class="mx-1 font-weight-light"
            :loading="loading"
            :disabled="loading"
            color="purple darken-3 white--text"
            @click="regeneratePdf">RECREATE PDF</v-btn>

          <v-btn
            v-if="siterequest.status === 'IMPORTED'"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="(typeof siterequest.response_pdf !== 'object')"
            @click="saveResponsePdf">SAVE</v-btn>

          <v-btn
            v-if="siterequest.status === 'NEW'"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!valid || !siterequested_drugs.length || loading"
            @click="request">REQUEST</v-btn>
          <v-btn
            v-if="siterequest.status === 'REQUESTED' && isAdmin"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading || unconfirmed"
            @click="confirm">CONFIRM</v-btn>

          <v-btn
            v-if="isAdmin && siterequest.status === 'CONFIRMED'"
            class="mx-1 font-weight-light"
            :loading="loading"
            :disabled="loading"
            color="primary"
            @click="send">SEND</v-btn>

          <v-btn
            v-if="isAdmin && ['CONFIRMED', 'SENT', 'REQUESTED', 'NEW'].includes(siterequest.status)"
            class="mx-1 red white--text font-weight-light"
            :disabled="loading"
            @click="cancel">CANCEL</v-btn>            

          <v-btn
            v-if="siterequest.status === 'SENT' "
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading || unconfirmed"
            @click="received(item)">CONFIRM</v-btn>

          <v-btn
            v-if="siterequest.response_pdf && ['RECEIVED', 'IMPORTED'].includes(siterequest.status) && (typeof siterequest.response_pdf !== 'object')"
            class="mx-1"
            color="purple darken-2 white--text"
            :href='`${BASE_URL}/pdf/${siterequest.response_pdf}`'>
            RESPONSE
              <v-icon color=''>mdi-file-pdf-box</v-icon> 
            </v-btn>

          <v-btn
            v-if="siterequest.status === 'RECEIVED' && isAdmin"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="(typeof siterequest.response_pdf !== 'object')"
            @click="saveResponsePdf">SAVE NEW RESPONSE PDF</v-btn>

            <v-btn
            v-if="canUpdate"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="$refs.upsert.save()">UPDATE</v-btn>


          <!-- NEW siterequest, sia EMN che i Site possono farlo,
            nel caso sia EMN si salta lo step della conferma o no? probabile -->
          <app-table
            title="Drugs"
            upsertTitle="Drug"
            v-if="siterequest.status === 'NEW' && !siterequest.is_transfer"
            :config="drugConfig"
            :filters="{ siterequest_id }"
            @loaded="siterequestLoaded"
            @saved="notifyForDrugPresence"
            no-search
            :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, country: siterequest.site.country_code }"/>

          <app-table
            title="Drug to transfer"
            v-if="siterequest.status === 'NEW' && siterequest.is_transfer"
            :config="drugTransfer"
            :filters="{ siterequest_id }"
            @loaded="siterequestLoaded"
            :hide-create='siterequested_drugs.length'
            no-search
            :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, country: siterequest.site.country_code, site_source_id: siterequest.site_source_id }"/>

          <!-- Ai site mostro sempre i farmaci richiesti -->
          <template v-if="user.role === 'Site' || user.role === 'Monitor'">

            <app-table
              v-if="siterequest.status !== 'NEW' && siterequest.status !== 'SENT' && siterequest.status !== 'RECEIVED' && siterequest.status !== 'IMPORTED'" 
              :config="drugRequestedConfig"
              title="Drugs"
              upsertTitle="Drug"
              :filters="{ siterequest_id }"
              hide-create
              hide-actions
              hide-upsert
              no-search />

            <!-- drugConfirmConfigSiteRequest sostituito per site request errorre ricezione -->
            <app-table
              v-if="siterequest.status === 'SENT' && !siterequest.is_transfer"
              title="Drugs sent"
              upsertTitle="Drug received"
              @loaded='siteresponseLoaded'
              :config="drugConfirmConfigSiteRequest"
              :filters="{ siterequest_id }"
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id }"
              no-search />
            <!-- drugConfirmConfigSiteRequest sostituito per site request errorre ricezione -->

            <app-table
              v-if="siterequest.status === 'SENT' && siterequest.is_transfer"
              @loaded='siteresponseLoaded'
              :config="drugTransferConfirm"
              :filters="{ siterequest_id }"
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, site_source_id: siterequest.site_source_id }"
              no-search />              

            <app-table
              v-if="siterequest.status === 'RECEIVED' || siterequest.status == 'IMPORTED'"
              :config="drugReceivedConfig"
              title="Drugs received"
              :filters="{ siterequest_id }"
              hide-create
              hide-actions
              hide-upsert
              no-search />              

          </template>

          <template v-if="isAdmin">
          <v-dialog
            v-if="siterequest.status === 'REQUESTED'"
            v-model="openAddRequestUpsert"
            max-width="650px">
              <app-upsert
                v-if="openAddRequestUpsert"
                title="Drugs to manually confirm"
                :item="{ siterequest_id, protocol_id: siterequest.protocol_id }"
                :config="drugAddRequestedConfig"
                modal
                @saved="$refs.requestedDrugs.getData()"
                @close="openAddRequestUpsert=false" />
            </v-dialog>
            <app-table
              v-if="siterequest.status === 'REQUESTED'"
              ref='requestedDrugs'
              title="Drugs requested"
              upsertTitle="Drug to confirm"
              :config="drugRequestedConfig"
              :filters="{ siterequest_id }"
              @loaded='siterequestLoaded'
              hide-create
              no-search
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id }">
              <template v-slot:headerActions>
                <v-btn
                  color="pink"
                  @click="openAddRequestUpsert = true">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>                
              </template>
            </app-table>

            <!-- CONFIRMED / NOT TRANSFER:
              - EMN ha confermato l'ordine di un centro, e' arrivata la mail al deposito o al fornitore
                che se e' abilitato alla compilazione online puo' farlo.
            -->
            <app-table
              v-if="siterequest.status === 'CONFIRMED' && !siterequest.is_transfer"
              upsert-max-width="800px"
              title="Drugs to ship"
              :config="drugConfirmConfig"
              upsertTitle="Drug to ship"
              :filters="{ siterequest_id }"
              @loaded="siteresponseLoaded"
              no-search
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id }" />

            <app-table
              v-if="siterequest.status === 'CONFIRMED' && siterequest.is_transfer"
              upsert-max-width="800px"
              title="Drug to transfer"
              :config="drugTransferConfirm"
              upsertTitle="Drug to transfer"
              :filters="{ siterequest_id }"
              @loaded="siteresponseLoaded"
              no-search
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, site_source_id: siterequest.site_source_id  }" />

            <!-- SENT / NOT TRANSFER -->
            <app-table
              v-if="siterequest.status === 'SENT' && !siterequest.is_transfer"
              title="Received drugs"
              upsertTitle="Received drug"
              @loaded='siteresponseLoaded'
              :config="drugAdminConfirmConfig"
              :filters="{ siterequest_id }"
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, country: siterequest.site.country_code }"
              no-search />

            <!-- SENT / TRANSFER -->
            <app-table
              v-if="siterequest.status === 'SENT' && siterequest.is_transfer"
              @loaded='siteresponseLoaded'
              :config="drugTransferConfirm"
              :filters="{ siterequest_id }"
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id, site_source_id: siterequest.site_source_id }"
              no-search />              


            <app-table
              v-if="siterequest.status === 'CANCELED' || siterequest.status === 'LOST'"
              title="Drug canceled"
              :config="drugRequestedConfig"
              :filters="{ siterequest_id }"
              :hidden-fields="{ siterequest_id, protocol_id: siterequest.protocol_id }"
              no-search
              hide-create
              hide-actions
            />


            <app-table
              v-if="siterequest.status === 'RECEIVED' || siterequest.status === 'IMPORTED'"
              title="Drugs received"
              :config="drugReceivedConfig"
              :filters="{ siterequest_id }"
              hide-upsert
              no-search />

          </template>

        </template>

      </app-upsert>

    </v-flex>

  </v-container>
</template>

<script>

import config from './config_upsert.js'
import nestedSiterequestConfig from './config_nested.js'
import drugConfig from './drug_index.js'
import drugTransfer from './drug_transfer.js'
import drugConfirmConfig from './drug_confirm.js'
//drugConfirmConfigSiteRequest sostituito per site request errorre ricezione
import drugConfirmConfigSiteRequest from './drug_confirm_siterequest.js'
//drugConfirmConfigSiteRequest sostituito per site request errorre ricezione
import drugAdminConfirmConfig from './drug_admin_confirm.js'
import drugTransferConfirm from './transfer_confirm.js'
import drugSentConfig from './drug_sent.js'
import drugReceivedConfig from './drug_received.js'
import drugRequestedConfig from './drug_requested'
import drugAddRequestedConfig from './drug_add_requested.js'

const { upsert, getDetail, getList } = require('@/api/base_entity')
const { requestSiteRequest, getSiterequestList, regenerateSiterequestPdf,
  confirmSiteRequest, storageConfirm, cancelSiteRequest } = require('@/api/siterequest')
const { drugOnSite } = require('@/api/balance')

import { mapState } from 'vuex'
const BASE_URL = process.env.VUE_APP_BASE_API

export default {
  inheritAttrs: false,
  data () {
    return {
      BASE_URL,
      config,
      drugConfig,
      drugTransfer,
      drugConfirmConfig,
      //drugConfirmConfigSiteRequest sostituito per site request errorre ricezione
      drugConfirmConfigSiteRequest,
      //drugConfirmConfigSiteRequest sostituito per site request errorre ricezione
      drugAdminConfirmConfig,
      drugTransferConfirm,
      drugSentConfig,
      drugRequestedConfig,
      drugReceivedConfig,
      drugAddRequestedConfig,
      nestedSiterequestConfig,
      getSiterequestList,
      siterequested_drugs: [],
      siteresponse_drugs: [],
      siterequest_id: null,
      openDrugs: false,
      openSite: false,
      siterequest: {},
      protocol: {},
      loaded: false,
      loading: false,
      unconfirmed: true,
      openAddRequestUpsert: false,
      openAuditTrail: false
    }
  },
  async created () {
    this.siterequest_id = Number(this.$route.params.siterequestId)
    this.siterequest = await getDetail('siterequest', { id: this.siterequest_id })
    if (this.siterequest.status === 'SPLITTED') {
      this.$router.replace('/siterequest')
      return
    }
    if (this.siterequest.status !== 'NEW') { // && this.siterequest.status !== 'SENT') {
      const ret= await getList('siterequest_has_drug', { siterequest_id: this.siterequest_id })
      this.siterequested_drugs = ret.data
    }
    if (this.siterequest.status === 'SENT') {
      const ret= await getList('siteresponse_has_drug', { siterequest_id: this.siterequest_id })
      this.siteresponse_drugs = ret.data
    }
    this.loaded = true
  },
  computed: {
    ...mapState(['user']),
    isAdmin () {
      return this.user.role === 'Administrator' || this.user.role === 'EMN User'
    },
    isPatchable () {
      const sr = this.siterequest
      return sr && sr.id && ['RECEIVED', 'IMPORTED', 'CANCELED', 'LOST'].includes(sr.status)
    },
    status () {
      if (this.siterequest.is_transfer) {
        const from_site = this.siterequest.from_site
        return `${this.siterequest.status} - SITE TRANSFER FROM ${from_site.name} ${this.siterequest.from_site.code}`
      }
      return this.siterequest.status
    },
    missingDrugs () {
      if (this.siterequest.status === 'SENT') {
        const missingSerials = this.siteresponse_drugs
          .filter(d => d.drug.serials_available && !d.serials)
          .map(d => d.lotto)
        
        // devo controllare che ci siano i seriali quando necessario
        if (missingSerials.length) {
          return [{
            label: `Missing required serials -> ${missingSerials.join(', ')}`,
            color: 'warning'
          }]
        }
      }      
      if (this.siterequest.status === 'CONFIRMED' || this.siterequest.status === 'SENT') {
        return this.siterequested_drugs.map(d => {
          const n_confirmed = this.siteresponse_drugs.reduce((tot, item) => tot + (item.drug_id === d.drug_id ? item.n_pieces : 0), 0)
          return {
            label: (d.drug?.packaging !== 'Labels' ? `${d.drug?.name} ${d.drug?.dose}${d.drug?.measure_unit}` : `${d.drug?.name} ${d.drug?.labels?.join(',')}` ) + ` (${n_confirmed} of ${d.n_confirmed})`,
            color: n_confirmed === d.n_confirmed ? 'green' : 'warning',
            n_requested: n_confirmed,
            n_confirmed: d.n_confirmed,
            completed: n_confirmed === d.n_confirmed
          }
        }).filter(d => d.n_confirmed)
      }
      return []

    },
    canUpdate(){
      return (this.isAdmin && this.siterequest.status !=='NEW')
    }
  },
  methods: {
    async updateSiteRequest(item){
       this.loading = true
      try {
        await upsert('siterequest',item,item.id)
        this.$router.go()
      } catch (e) {
        console.error(e)
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error' })
        this.loading = false
      }
    },
    async notifyForDrugPresence (item) {
      if (this.user.role === 'Site') {
        // controllo se c'e' gia' quel farmaco nel centro e nel caso avviso se l'utente e' il Site
        const ret = await drugOnSite({ site_id: this.siterequest.site_id, drug_id: item.item.drug_id })
        if (ret && Number(ret.n) > 0) {
          this.$root.$emit('openSnackbar', {
            message: `Warning, this site has already <u>${ret.n}</u> boxes/sheet of drug <u>${ret.drug_name}</u>`,
            color: 'warning'
          })
        }
      }
    },
    async siteResponseLoaded (data) {
      if (this.siterequest.status === 'CONFIRMED') {
        this.siteresponse_drugs = data
      }
    },
    async saveResponsePdf () {
      this.loading = true
      const formData = new FormData()
      formData.append('response_pdf', this.siterequest.response_pdf)
      try {
        await upsert(`siterequest/save_response_pdf/${this.siterequest_id}`, formData)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false        
      }
    },
    async request () {
      if (!this.$refs.upsert.validate()) return
      this.loading = true
      await requestSiteRequest (this.siterequest)
      this.$router.go()
    },
    async confirm () {
      if (!this.$refs.upsert.validate()) return
      this.loading = true
      try {
        await confirmSiteRequest(this.siterequest)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false
      }
    },
    async cancel () {
      this.siterequest.canceling = true
      if (!this.$refs.upsert.validate()) return
      const ret = await this.$root.$confirm.open('Cancel Site Request', `Are you sure to continue?</u>`)
      if (!ret) { return }
      this.loading = true
      await cancelSiteRequest(this.siterequest)
      this.$router.go()
    },
    async regeneratePdf () {
      this.loading = true
      try {
        await regenerateSiterequestPdf(this.siterequest)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
      }      
      this.loading = false
    },
    async send () {
      if (!this.$refs.upsert.validate()) return

      // check missing drugs, first
      if (this.missingDrugs.find(d => !d.completed && d.n_requested > d.n_confirmed)) {
        // const missing = this.missingDrugs.find(d => !d.completed && d.n_confirmed > d.n_requested)
        this.$root.$emit("openSnackbar", {
          message: `Please insert requested drugs quantity`,
          color: "error"
        })
        return
      } else if (this.missingDrugs.find(d => !d.completed)) {
        const ret = await this.$root.$confirm.open('Missing informations', `Some drugs details are missing, are you sure to continue?</u>`)
        if (!ret) { return }
      }
      this.loading = true


      try {
        await storageConfirm(this.siterequest)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false
      }
    },

    // il centro invia il pdf che conferma la ricezione dei farmaci
    async received (item) {
      if (!this.$refs.upsert.validate()) return
      // check first missing drugs
      const missingDrugs = this.missingDrugs.find(d => !d.completed)
      if (missingDrugs) {
        //if (!this.isAdmin) {
          this.$root.$emit("openSnackbar", {
            message: `Please insert all received drugs`,
            color: "error"
          })
          return
        // } else {
        //   const ret = await this.$root.$confirm.open('Confirm', 'Are you sure to continue? You did not insert all requested drugs', { color: 'red' })
        //   if (!ret) return
        // }
      }

      this.loading = true
      const formData = new FormData()
      formData.append('response_pdf', item['response_pdf'])
      formData.append('response_date', item['response_date'])
      try {
        await upsert(`siterequest/received/${this.siterequest_id}`, formData)
        this.$router.go()
      } catch (e) {
        console.error(e)
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error' })
        this.loading = false
      }
    },
    saved ({ data }) {
      this.request_id = data.id
      this.request = data
      this.isNew = false      
    },

    siteresponseLoaded (items) {
      this.siteresponse_drugs = items
      // cerco i farmaci a cui nn sono stati inseriti i seriali
      const missingSerials = items.find(d => d.drug.serials_available && !d.serials)
      this.unconfirmed = !!missingSerials
    },

    siterequestLoaded (items) {
      this.siterequested_drugs = items
      // controllo che tutte siano confermate e attivo il tasto confirm
      const unconfirmed = items.find(i => i.n_confirmed === null)

      // controllo anche che ci sia almeno qualcosa
      const n_total = items.reduce( (v, current) => v + current.n_confirmed , 0)
      this.unconfirmed = !!unconfirmed || n_total === 0
    },

  }
}
</script>
